import clsx from 'clsx'
import { Panel } from './slp/Panel'
import { StatusLamp, type LampStatus } from './slp/StatusLamp'
import '../styles/slp.css'

/**
 * Client Analyzer — SLP-style connection-health panel (rt UI v2 spec,
 * Recording-mode screenshot). Rows: RTT / packet loss / jitter / actual
 * bitrate / disk free, each with value + unit + a yellow/red threshold lamp.
 *
 * Pure presentation: callers feed `metrics` (studio surface renders one per
 * remote end, actor surface renders its own). Null/undefined metric = gray
 * "off" lamp + em-dash value.
 *
 * Default thresholds (§7.1-aligned; same RTT bands as the JoinPage
 * pre-flight, disk floor = DISK_NEED_MB from pre-flight):
 *   rtt      warn ≥150 ms      error ≥250 ms
 *   loss     warn ≥1 %         error ≥3 %
 *   jitter   warn ≥30 ms       error ≥60 ms
 *   bitrate  warn ≤80 %        error ≤50 % of target (default target 128 kbps)
 *   disk     warn ≤3110 MB     error ≤1555 MB
 * Override any band via the `thresholds` prop.
 */

export interface AnalyzerMetrics {
  rttMs?: number | null
  packetLossPct?: number | null
  jitterMs?: number | null
  bitrateKbps?: number | null
  diskFreeMb?: number | null
}

export interface AnalyzerThresholds {
  rttWarnMs: number
  rttErrorMs: number
  lossWarnPct: number
  lossErrorPct: number
  jitterWarnMs: number
  jitterErrorMs: number
  /** Bitrate is judged as a fraction of `targetBitrateKbps`. */
  bitrateWarnRatio: number
  bitrateErrorRatio: number
  diskWarnMb: number
  diskErrorMb: number
}

export const DEFAULT_ANALYZER_THRESHOLDS: AnalyzerThresholds = {
  rttWarnMs: 150,
  rttErrorMs: 250,
  lossWarnPct: 1,
  lossErrorPct: 3,
  jitterWarnMs: 30,
  jitterErrorMs: 60,
  bitrateWarnRatio: 0.8,
  bitrateErrorRatio: 0.5,
  diskWarnMb: 3110,
  diskErrorMb: 1555,
}

type Row = {
  metric: string
  label: string
  value: string
  status: LampStatus
}

function fmt(n: number, digits = 0): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 })
}

/** Higher-is-bad metric → lamp status. */
function bad(v: number, warn: number, error: number): LampStatus {
  return v >= error ? 'error' : v >= warn ? 'warn' : 'ok'
}

/** Lower-is-bad metric → lamp status. */
function low(v: number, warn: number, error: number): LampStatus {
  return v <= error ? 'error' : v <= warn ? 'warn' : 'ok'
}

export function ClientAnalyzer({
  metrics,
  title = 'Client Analyzer',
  /** Who these numbers belong to, e.g. "Remote User 1" — shown as a sub-line. */
  subject,
  targetBitrateKbps = 128,
  thresholds,
  className,
}: {
  metrics: AnalyzerMetrics
  title?: string
  subject?: string
  targetBitrateKbps?: number
  thresholds?: Partial<AnalyzerThresholds>
  className?: string
}) {
  const t = { ...DEFAULT_ANALYZER_THRESHOLDS, ...thresholds }
  const m = metrics

  const rows: Row[] = [
    {
      metric: 'rtt',
      label: 'RTT',
      value: m.rttMs == null ? '—' : `${fmt(m.rttMs)} ms`,
      status: m.rttMs == null ? 'off' : bad(m.rttMs, t.rttWarnMs, t.rttErrorMs),
    },
    {
      metric: 'loss',
      label: 'Packet loss',
      value: m.packetLossPct == null ? '—' : `${m.packetLossPct.toFixed(1)} %`,
      status: m.packetLossPct == null ? 'off' : bad(m.packetLossPct, t.lossWarnPct, t.lossErrorPct),
    },
    {
      metric: 'jitter',
      label: 'Jitter',
      value: m.jitterMs == null ? '—' : `${fmt(m.jitterMs, 1)} ms`,
      status: m.jitterMs == null ? 'off' : bad(m.jitterMs, t.jitterWarnMs, t.jitterErrorMs),
    },
    {
      metric: 'bitrate',
      label: 'Bitrate',
      value: m.bitrateKbps == null ? '—' : `${fmt(m.bitrateKbps)} kbit/s`,
      status:
        m.bitrateKbps == null
          ? 'off'
          : low(
              m.bitrateKbps,
              targetBitrateKbps * t.bitrateWarnRatio,
              targetBitrateKbps * t.bitrateErrorRatio,
            ),
    },
    {
      metric: 'disk',
      label: 'Disk free',
      value: m.diskFreeMb == null ? '—' : `${fmt(m.diskFreeMb)} MB`,
      status: m.diskFreeMb == null ? 'off' : low(m.diskFreeMb, t.diskWarnMb, t.diskErrorMb),
    },
  ]

  return (
    <Panel title={title} className={className}>
      <div data-testid="rt-client-analyzer">
        {subject ? (
          <div className="mb-1.5 text-[12px] font-semibold text-[var(--slp-text-dim)]">{subject}</div>
        ) : null}
        <table className="w-full border-collapse text-[13px]">
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.metric}
                data-metric={r.metric}
                data-status={r.status}
                className="border-b border-[var(--slp-border)] last:border-b-0"
              >
                <td className="py-1 pr-2 text-[var(--slp-text-dim)]">{r.label}</td>
                <td
                  className={clsx(
                    'py-1 pr-2 text-right font-mono tabular-nums',
                    r.status === 'error'
                      ? 'font-semibold text-[var(--slp-lamp-red)]'
                      : r.status === 'warn'
                        ? 'font-semibold text-[#b07b00]'
                        : 'text-[var(--slp-text)]',
                  )}
                >
                  {r.value}
                </td>
                <td className="w-4 py-1 text-right">
                  <StatusLamp status={r.status} size="sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}
